// @ts-check
/**
 * ConfigGenerator - Converts maze layout to gallery configuration
 * Assigns videos to painting slots and generates final config
 *
 * Typed contract: inputs/outputs are checked against the shared typedefs in
 * src/utils/types.js via `// @ts-check` + `npm run typecheck`.
 */

/**
 * @typedef {import('./types.js').MazeLayout} MazeLayout
 * @typedef {import('./types.js').PaintingSlot} PaintingSlot
 * @typedef {import('./types.js').VideoManifestEntry} VideoManifestEntry
 * @typedef {import('./types.js').PaintingConfig} PaintingConfig
 * @typedef {import('./types.js').GalleryConfig} GalleryConfig
 * @typedef {import('./types.js').Scale} Scale
 */

/**
 * @typedef {object} GenerateSettings
 * @property {Scale} [defaultScale]
 * @property {boolean} [shuffleVideos]
 * @property {number} [seed] - Required at runtime when shuffleVideos is
 *   true (shuffleArray throws otherwise, see below) - not modeled as a
 *   conditional type here since these settings are a plain options bag, not
 *   a discriminated union.
 */

export class ConfigGenerator {
  /**
   * Generate complete gallery config from maze layout and videos
   *
   * @param {Array<VideoManifestEntry>} videoList
   * @param {MazeLayout} mazeLayout
   * @param {GenerateSettings} [settings]
   * @returns {GalleryConfig}
   */
  static generateFromVideos(videoList, mazeLayout, settings = {}) {
    const { walls, paintingSlots, startPosition } = mazeLayout;

    // Assign videos to painting slots
    const paintings = this.assignVideosToSlots(videoList, paintingSlots, settings);

    // Build final configuration
    const config = {
      startPosition: startPosition,
      walls: walls,
      paintings: paintings,
      metadata: {
        generatedAt: new Date().toISOString(),
        seed: mazeLayout.seed,
        gridSize: mazeLayout.gridSize,
        totalSize: mazeLayout.totalSize,
        paintingCount: paintings.length,
        wallCount: walls.length
      }
    };

    console.log('Generated configuration:', {
      paintings: config.paintings.length,
      walls: config.walls.length,
      gridSize: config.metadata.gridSize
    });

    return config;
  }

  /**
   * Assign videos to painting slots
   *
   * @param {Array<VideoManifestEntry>} videoList
   * @param {Array<PaintingSlot>} paintingSlots
   * @param {GenerateSettings} settings
   * @returns {Array<PaintingConfig>}
   */
  static assignVideosToSlots(videoList, paintingSlots, settings) {
    const paintings = [];
    const defaultScale = settings.defaultScale || { width: 2, height: 3.52 };

    // Shuffle video list for variety if desired
    // `settings.seed` is optional on GenerateSettings but shuffleArray
    // requires a finite number (throws otherwise, see shuffleArray below);
    // the settings bag doesn't statically encode "seed is required when
    // shuffleVideos is true", so this cast documents that call-time
    // invariant instead of loosening shuffleArray's own contract.
    const shuffledVideos = settings.shuffleVideos
      ? this.shuffleArray([...videoList], /** @type {number} */ (settings.seed))
      : videoList;

    for (let i = 0; i < paintingSlots.length; i++) {
      const slot = paintingSlots[i];
      const video = shuffledVideos[i % shuffledVideos.length]; // Cycle through videos if more slots than videos

      const painting = this.createPaintingConfig(video, slot, i, defaultScale);
      paintings.push(painting);
    }

    return paintings;
  }

  /**
   * Create painting configuration from video and slot
   *
   * Carries two distinct ids, deliberately kept separate:
   * - `id` ("painting-N"): 1-based display numbering derived from position in the
   *   slot list. Used for logging/UI (see VideoPainting). Reassigning videos or
   *   reshuffling does not change what a slot IS, so this is not layout identity.
   * - `slotId`: the MazeGenerator-assigned layout identity (`room_{row}_{col}_{wall}`,
   *   see MazeGenerator's "Identity contract"). Gallery uses this to resolve a
   *   room's painting slots to live VideoPainting instances without comparing
   *   floating point coordinates.
   *
   * @param {VideoManifestEntry} video
   * @param {PaintingSlot} slot
   * @param {number} index
   * @param {Scale} defaultScale
   * @returns {PaintingConfig}
   */
  static createPaintingConfig(video, slot, index, defaultScale) {
    return {
      id: `painting-${index + 1}`,
      slotId: slot.id,
      title: video.title || `Artwork ${index + 1}`,
      description: video.description || 'Untitled',
      // `filename` is required on VideoManifestEntry and always a non-empty
      // string in every real producer (manifest.json entries, the fallback
      // list); `video.url` is legacy defensive code no producer sets today
      // (see VideoManifestEntry). TS can't statically rule out an empty
      // `filename` making the chain fall through to the optional `url`, so
      // the result type comes out as `string | undefined` - cast rather
      // than making PaintingConfig.videoUrl optional, since every real
      // painting config does carry a concrete videoUrl.
      videoUrl: /** @type {string} */ (video.videoUrl || video.filename || video.url),
      position: slot.position,
      rotation: slot.rotation,
      scale: video.scale || defaultScale,
      frameStyle: slot.frameStyle
    };
  }

  /**
   * Load video manifest from JSON file
   *
   * @param {string} [manifestPath]
   * @returns {Promise<Array<VideoManifestEntry>>}
   */
  static async loadVideoManifest(manifestPath = '/videos/manifest.json') {
    try {
      const response = await fetch(manifestPath);
      if (!response.ok) {
        throw new Error(`Failed to load video manifest: ${response.statusText}`);
      }

      const manifest = await response.json();
      console.log(`Loaded ${manifest.videos.length} videos from manifest`);

      // Convert manifest entries to full URLs
      return manifest.videos.map(video => ({
        ...video,
        videoUrl: video.videoUrl || `/videos/${video.filename}`
      }));
    } catch (error) {
      console.error('Error loading video manifest:', error);
      throw error;
    }
  }

  /**
   * Shuffle array using seeded random (for reproducibility)
   *
   * `seed` is required - this is the one place a caller could accidentally
   * desync the video shuffle from the maze seed (see SeedResolver, which is
   * the single source of truth both are meant to share). A missing or
   * non-numeric seed fails loudly instead of silently falling back to
   * Date.now(), which would produce a maze/video pairing that cannot be
   * reproduced from a shared link.
   *
   * @template T
   * @param {Array<T>} array
   * @param {number} seed
   * @returns {Array<T>}
   */
  static shuffleArray(array, seed) {
    if (!Number.isFinite(seed)) {
      throw new Error(`ConfigGenerator.shuffleArray requires a numeric seed, got: ${seed}`);
    }

    const rng = this.createSeededRNG(seed);
    const shuffled = [...array];

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  /**
   * Seeded random number generator (same as MazeGenerator)
   *
   * @param {number} seed
   * @returns {() => number}
   */
  static createSeededRNG(seed) {
    return function() {
      let t = seed += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /**
   * Export configuration to JSON string
   *
   * @param {GalleryConfig} config
   * @param {boolean} [pretty]
   * @returns {string}
   */
  static exportToJSON(config, pretty = true) {
    return JSON.stringify(config, null, pretty ? 2 : 0);
  }

  /**
   * Validate configuration structure
   *
   * Dead code - never called anywhere in the codebase (verified by
   * grepping for `validateConfig` outside this file). Typed like the rest
   * of this class for `// @ts-check` consistency; wiring it up or deleting
   * it is out of scope for this story - see story 002's Technical Notes.
   *
   * @param {GalleryConfig} config
   * @returns {boolean}
   */
  static validateConfig(config) {
    const errors = [];

    // Check required fields
    if (!config.startPosition) {
      errors.push('Missing startPosition');
    }
    if (!config.walls || !Array.isArray(config.walls)) {
      errors.push('Missing or invalid walls array');
    }
    if (!config.paintings || !Array.isArray(config.paintings)) {
      errors.push('Missing or invalid paintings array');
    }

    // Validate paintings
    if (config.paintings) {
      config.paintings.forEach((painting, index) => {
        if (!painting.id) errors.push(`Painting ${index} missing id`);
        if (!painting.videoUrl) errors.push(`Painting ${index} missing videoUrl`);
        if (!painting.position) errors.push(`Painting ${index} missing position`);
        if (!painting.rotation) errors.push(`Painting ${index} missing rotation`);
      });
    }

    // Validate walls
    if (config.walls) {
      config.walls.forEach((wall, index) => {
        if (!wall.position) errors.push(`Wall ${index} missing position`);
        if (!wall.rotation) errors.push(`Wall ${index} missing rotation`);
        if (!wall.dimensions) errors.push(`Wall ${index} missing dimensions`);
      });
    }

    if (errors.length > 0) {
      console.error('Configuration validation errors:', errors);
      return false;
    }

    console.log('✓ Configuration valid');
    return true;
  }

  /**
   * Generate fallback video list (when manifest not available)
   *
   * @param {number} [count]
   * @returns {Array<VideoManifestEntry>}
   */
  static generateFallbackVideoList(count = 9) {
    const videos = [];
    const titles = [
      'Abstract Motion', 'Digital Dreams', 'Void Echoes',
      'Temporal Flux', 'Neon Reverie', 'Cyber Garden',
      'Infinite Loop', 'Data Stream', 'Virtual Horizon',
      'Pixel Paradise', 'Code Poetry', 'Synthetic Soul'
    ];

    for (let i = 0; i < count; i++) {
      videos.push({
        filename: `video-${i + 1}.mp4`,
        videoUrl: `/videos/video-${i + 1}.mp4`,
        title: titles[i % titles.length],
        description: `Generative artwork #${i + 1}`,
        scale: { width: 2, height: 3.52 }
      });
    }

    return videos;
  }
}
